// @flow
import React, { Component, Fragment } from 'react';
import classNames from 'classnames/bind';
import Modal from '../../../../elements/Modal/Modal';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import PropTypes from 'prop-types';

import style from './BonusesModal.scss';
import icons from '../../../../assets/styles/icons.scss';
import blackHand from '../../../../assets/img/bonuses/black_hand.png';

import type { TravelerReward } from '../../duck';

type Props = {
  +open: boolean;
  +handleClose: () => void;
  +travelerReward: TravelerReward;
};

const messages = defineMessages({
  orderSingular: {
    id: 'bonusesModal.order.singular',
  },
  orderPlural: {
    id: 'bonusesModal.order.plural',
  },
  progressionText1: {
    id: 'bonusesModal.progression.text1',
  },
  progressionText2: {
    id: 'bonusesModal.progression.text2',
  },
  progressionText3: {
    id: 'bonusesModal.progression.text3',
  },
  progressionText4: {
    id: 'bonusesModal.progression.text4',
  },
  bonus1: {
    id: 'bonusesModal.bonus1',
  },
  bonus2: {
    id: 'bonusesModal.bonus2',
  },
  bonus3: {
    id: 'bonusesModal.bonus3',
  },
  bonus4: {
    id: 'bonusesModal.bonus4',
  },
});

const MAX_PRODUCTS = 50;

export default class BonusesModal extends Component<Props> {
  render() {
    const { handleClose, open, travelerReward } = this.props;
    const { nextReward, progress, reservationCount, reward, total } = travelerReward;
    const missingOrders = total - progress;
    const tier = Math.floor(reservationCount / total);
    return (
      <Modal handleClose={handleClose}
             open={open}>
        <FormattedMessage id="bonusesModal.title1">{
          txt => <p className={classNames(style.title, style.orange)}>{txt}</p>
        }</FormattedMessage>
        <FormattedMessage id="bonusesModal.title2">{
          txt => <p className={style.title}>{txt}</p>
        }</FormattedMessage>
        {reservationCount < MAX_PRODUCTS && <section className={style.progress}>
          <FormattedMessage id="bonusesModal.inProgress">{
            txt => <p className={classNames(style.title, style.left)}>{txt}</p>
          }</FormattedMessage>
          <section className={style.counter}>
            <section className={style.productsCounter}>
              <article style={{ width: `${Math.round((progress / total) * 100)}%` }}/>
              <span className={style.productsDot}
                    style={{ left: `${Math.round((progress / total) * 100)}%` }}/>
            </section>
            <p className={classNames(style.productsCount, style.inlineBlock, style.bold)}>{`${progress}/${total}`}</p>
            <FormattedMessage id="bonusesModal.order.plural">{
              txt => <p className={style.inlineBlock}>{txt}</p>
            }</FormattedMessage>
          </section>
          <section className={style.nextStep}>
            <p className={classNames(style.inlineBlock, style.bold)}>
              {this.context.intl.formatMessage(messages.progressionText1)}&nbsp;</p>
            <p className={classNames(style.inlineBlock, style.bold)}>{missingOrders}&nbsp;</p>
            <p className={classNames(style.inlineBlock, style.bold)}>
              {missingOrders !== 1 ? this.context.intl.formatMessage(messages.orderPlural) :
                this.context.intl.formatMessage(messages.orderSingular)}&nbsp;</p>
            {reservationCount !== 0 &&
            <p className={style.inlineBlock}>{this.context.intl.formatMessage(messages.progressionText2)}&nbsp;</p>}
            <p className={style.inlineBlock}>{this.context.intl.formatMessage(messages.progressionText3)}&nbsp;</p>
            <p className={style.inlineBlock}>$ {nextReward - parseInt(reward, 10)}&nbsp;</p>
            <p className={style.inlineBlock}>{this.context.intl.formatMessage(messages.progressionText4)}</p>
          </section>
        </section>}
        {reservationCount >= MAX_PRODUCTS &&
        <section className={style.completed}>
          <img src={blackHand} alt="black-hand"/>
          <FormattedMessage id="bonusesModal.bonusesCompleted">{
            txt => <p>{txt}</p>
          }</FormattedMessage>
        </section>}
        <FormattedMessage id="bonusesModal.bonuses.title">{
          txt => <p className={classNames(style.title, style.bonus)}>{txt}:</p>
        }</FormattedMessage>
        <section className={style.bonusTier}>
          <article className={classNames({ [style.checked]: tier >= 1 })}>
            <i className={icons.symbolCheck}/>
          </article>
          <FormattedMessage id="bonusesModal.bonus1">{
            txt => <p>{txt}</p>
          }</FormattedMessage>
        </section>
        <section className={style.bonusTier}>
          <article className={classNames({ [style.checked]: tier >= 2 })}>
            <i className={icons.symbolCheck}/>
          </article>
          <FormattedMessage id="bonusesModal.bonus2">{
            txt => <p>{txt}</p>
          }</FormattedMessage>
        </section>
        <section className={style.bonusTier}>
          <article className={classNames({ [style.checked]: tier >= 3 })}>
            <i className={icons.symbolCheck}/>
          </article>
          <FormattedMessage id="bonusesModal.bonus3">{
            txt => <p>{txt}</p>
          }</FormattedMessage>
        </section>
        <section className={style.bonusTier}>
          <article className={classNames({ [style.checked]: tier === 4 })}>
            <i className={icons.symbolCheck}/>
          </article>
          <FormattedMessage id="bonusesModal.bonus4">{
            txt => <p>{txt}</p>
          }</FormattedMessage>
        </section>
        <section className={style.bonusTier}>
          <article className={classNames({ [style.checked]: tier === 5 })}>
            <i className={icons.symbolCheck}/>
          </article>
          <FormattedMessage id="bonusesModal.bonus5">{
            txt => <p>{txt}</p>
          }</FormattedMessage>
        </section>
        <section className={style.earningsWrap}>
          <div className={style.awardCounter}>
            <svg width="40px" height="40px" viewBox="0 0 40 40" className="donut">
              <circle style={{ display: 'block' }} cx="20" cy="20" r="17.5" fill="transparent"
                      stroke="#EEEEEE" strokeWidth="5"/>
              <circle className={classNames({ [style.visible]: tier >= 1 })}
                      cx="20" cy="20" r="17.5" fill="transparent"
                      stroke="#C8E6C9" strokeWidth="5" strokeDasharray="21.991 87.964" strokeDashoffset="27.488"/>
              <circle className={classNames({ [style.visible]: tier >= 2 })}
                      cx="20" cy="20" r="17.5" fill="transparent"
                      stroke="#A5D6A7" strokeWidth="5" strokeDasharray="21.991 87.964" strokeDashoffset="5.497"/>
              <circle className={classNames({ [style.visible]: tier >= 3 })}
                      cx="20" cy="20" r="17.5" fill="transparent"
                      stroke="#81C784" strokeWidth="5" strokeDasharray="21.991 87.964" strokeDashoffset="-16.493"/>
              <circle className={classNames({ [style.visible]: tier >= 4 })}
                      cx="20" cy="20" r="17.5" fill="transparent"
                      stroke="#66BB6A" strokeWidth="5" strokeDasharray="21.991 87.964" strokeDashoffset="-38.484"/>
              <circle className={classNames({ [style.visible]: tier >= 5 })}
                      cx="20" cy="20" r="17.5" fill="transparent"
                      stroke="#4CAF50" strokeWidth="5" strokeDasharray="21.991 87.964" strokeDashoffset="-60.475"/>
              <circle style={{ display: 'block' }} r="2.5" fill="#388E3C"
                      cx={Math.cos((Math.PI / 2) - (tier * (2 * Math.PI / 5))) * 17.5 + 20}
                      cy={Math.sin((Math.PI / 2) - (tier * (2 * Math.PI / 5))) * -17.5 + 20}/>
            </svg>
            <article>$</article>
          </div>
          <FormattedMessage id="bonusesModal.progression.text5">{
            txt => <p className={style.earningsText}>{txt}:</p>
          }</FormattedMessage>
          <p className={classNames(style.earnings, style.inlineBlock)}>{`$ ${reward}`}</p>
        </section>
        <section className={style.terms}>
          <FormattedMessage id="bonusesModal.terms">{
            txt => <a target="_blank"
                      href="https://help.qempo.com/article/7d9m9iufqp-c-mo-funcionan-las-bonificaciones-para-viajeros">{txt}</a>
          }</FormattedMessage>
        </section>
      </Modal>
    );
  }
}

BonusesModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  travelerReward: PropTypes.object.isRequired,
};

BonusesModal.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/flight/components/BonusesModal/BonusesModal.jsx